import type { UserPrefs } from "@/hooks/useUserPreferences";

/**
 * The delivery-channel field mapping, in one place (Issue #470).
 *
 * Four things Kin sends, two channels each. There is deliberately no global
 * "delivery channel" column — the Settings chooser is one control writing
 * eight per-feature booleans:
 *
 *              push                                  text
 *   morning    notifications_morning_enabled         sms_morning_enabled
 *   weekly     notifications_weekly_enabled          sms_weekly_enabled
 *   tasks      notifications_task_updates_enabled    sms_task_updates_enabled
 *   ready      notifications_ready_enabled           sms_ready_enabled
 *
 * A row is ON when EITHER of its fields is true, which is what keeps "is Kin
 * sending this?" and "how does it arrive?" independent of each other.
 *
 * ── `ready` is the newest row (#109) ────────────────────────────────────────
 *
 * "Ready for you": a result she asked for coming back — a forwarded email Kin
 * Inbox finished parsing, a Beauty Analyzer render. Those two shipped riding
 * on the `tasks` pair (#832/#834), so switching restock nudges off also
 * switched off the answer to something she started ninety seconds ago, under a
 * toggle whose subtext says "Meal plans, restocks, reminders". Its server-side
 * half is READY_COLUMNS in supabase/functions/_shared/notify.ts; the two must
 * name the same columns, and src/lib/__tests__/deliveryChannel.test.ts pins
 * that they do.
 *
 * This module exists because THREE surfaces finish SMS verification and each
 * had its own idea of what that means. HowKinReachesYou derived the answer from
 * the current state; MorningNotesCard and ProSetupFlowModal both hardcoded
 * `{ sms_morning_enabled: true, sms_task_updates_enabled: true }`. Same event,
 * three answers, and two of them overrode choices she'd already made.
 */

export type DeliveryFeature = "morning" | "weekly" | "tasks" | "ready";

export const DELIVERY_FEATURES: DeliveryFeature[] = ["morning", "weekly", "tasks", "ready"];

export const PUSH_FIELD: Record<DeliveryFeature, keyof UserPrefs> = {
  morning: "notifications_morning_enabled",
  weekly: "notifications_weekly_enabled",
  tasks: "notifications_task_updates_enabled",
  ready: "notifications_ready_enabled",
};

export const TEXT_FIELD: Record<DeliveryFeature, keyof UserPrefs> = {
  morning: "sms_morning_enabled",
  weekly: "sms_weekly_enabled",
  tasks: "sms_task_updates_enabled",
  ready: "sms_ready_enabled",
};

/**
 * Best-effort IANA zone for this device.
 *
 * Lives here rather than in a component because EVERY surface that enables a
 * delivery needs it, and until Aug 4 2026 only one of them set it.
 */
export function resolveTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Should this session backfill `notifications_timezone`?
 *
 * Aug 4, 2026. Both dispatcher_get_users_due_for_* RPCs require the column
 * NOT NULL, and it has no DB default and no trigger — so a user whose row was
 * created without it is invisible to the dispatcher forever. She gets no
 * morning note and no weekly nudge, silently, and nothing in the app hints at
 * why.
 *
 * Before this, the column was only ever written as a side effect of changing a
 * preference: Settings' persist(), or a post-verify write. A user who opted into
 * texts through onboarding and never touched Settings had no reason to trigger
 * either, so she stayed stuck. Two live users were in exactly that state when
 * this was written.
 *
 * BACKFILL ONLY — never overwrite an existing zone. A mom reading Kin from a
 * hotel in another timezone should not have tomorrow's 7am note silently moved
 * to hotel-local time by the act of opening the app. Deliberate writes
 * (Settings, verification) still refresh it, because those are moments she is
 * actively choosing how Kin reaches her.
 */
export function shouldBackfillTimezone(args: {
  loading: boolean;
  hasUser: boolean;
  storedTimezone: string | null | undefined;
}): boolean {
  if (args.loading || !args.hasUser) return false;
  return !args.storedTimezone;
}

/** A row is on when either of its two channel fields is true. */
export function isRowOn(prefs: Partial<UserPrefs>, f: DeliveryFeature): boolean {
  return !!prefs[PUSH_FIELD[f]] || !!prefs[TEXT_FIELD[f]];
}

/**
 * What to write once a phone number has just been verified.
 *
 * Every caller states its own intent. There is no inference here, because two
 * rounds of getting this wrong were both inference bugs:
 *
 *   `promised`     features this surface's copy explicitly offered to text.
 *                  Enabled even when the row is currently off — she was shown
 *                  the offer and said yes, and the flow's receipt says so.
 *
 *   `carryOnRows`  also add text to rows she ALREADY receives. Only for
 *                  surfaces whose copy covers them.
 *
 * ── Why "off stays off" is the whole point ──────────────────────────────────
 *
 * The two flows this replaces wrote `sms_morning_enabled: true` AND
 * `sms_task_updates_enabled: true` unconditionally. So: switch Task updates off
 * in Settings, verify a number later from the Morning Notes chip, and Task
 * updates silently comes back on — a row is on when either field is true, and
 * the chip had just set the text one. From a flow that only ever mentions
 * mornings. An explicit off is a decision she made; a verification flow does not
 * get to undo it. `carryOnRows` therefore never resurrects an off row.
 *
 * ── Why there is no "nothing is on" fallback ────────────────────────────────
 *
 * An earlier cut of this defaulted to morning-by-text when no row was on, to
 * keep ProSetupFlowModal's receipt ("✓ Kin will text your mornings at ...")
 * honest. Copilot caught both halves of why that was wrong:
 *
 *   1. It barely ever fired. notifications_task_updates_enabled DEFAULTS TRUE,
 *      so a brand-new Pro user already has the tasks row on — the fallback was
 *      skipped and morning texts were never enabled, leaving the receipt lying
 *      in exactly the case it was written for.
 *   2. Where it did fire — every row deliberately off — it switched Morning Note
 *      back on, violating the off-stays-off rule two paragraphs up.
 *
 * The promise now lives with the surface that makes it. ProSetupFlowModal and
 * the Morning Notes chip both pass `promised: ["morning"]` because both say
 * mornings out loud ("Want your mornings by text?" / "Want this as a text
 * tomorrow?"). Settings passes `carryOnRows` instead: its entry point is a plain
 * "Set up texts" that promises no particular feature, so it adds text to what
 * she already gets and nothing more.
 *
 * If any of that copy changes, the argument at that call site changes with it.
 *
 * Push is never touched, by any caller. Verifying a phone number says nothing
 * about push, and switching it off would be the same overreach pointed the
 * other way.
 *
 * Returns only fields that actually change, and `{}` when nothing does, so
 * callers can skip a pointless write.
 */
export function buildPostVerifyPatch(
  prefs: Partial<UserPrefs>,
  opts: { promised?: DeliveryFeature[]; carryOnRows?: boolean } = {},
): Partial<UserPrefs> {
  const { promised = [], carryOnRows = false } = opts;
  const patch: Record<string, boolean | string> = {};

  const enable = (f: DeliveryFeature) => {
    if (prefs[TEXT_FIELD[f]]) return; // already texting; nothing to change
    patch[TEXT_FIELD[f] as string] = true;
  };

  for (const f of promised) enable(f);

  if (carryOnRows) {
    for (const f of DELIVERY_FEATURES) {
      if (isRowOn(prefs, f)) enable(f); // an explicit off stays off
    }
  }

  // ── notifications_timezone: without this she is undispatchable ────────────
  //
  // Both dispatcher_get_users_due_for_* RPCs require notifications_timezone
  // IS NOT NULL, and the column has no DB default, no trigger, and (until now)
  // exactly ONE writer in the entire codebase: HowKinReachesYou.persist().
  //
  // So a brand-new Pro user who opted into texts through ProSetupFlowModal or
  // the Morning Notes chip — and never opened Settings — had it NULL and got
  // nothing, forever. Existing users only have it because toggling something in
  // Settings happened to set it as a side effect. That coupling is why this went
  // unnoticed: every path anyone tested went through Settings first.
  //
  // Refreshed on every enabling write, matching what persist() already does, so
  // a mom who moves timezones gets her notes at the right local hour.
  const timezone = resolveTimezone();
  const wantsTimezone = Object.keys(patch).length > 0 || !prefs.notifications_timezone;
  if (timezone) {
    if (wantsTimezone) patch.notifications_timezone = timezone;
  } else if (wantsTimezone) {
    // Deliberately NOT defaulting to "UTC" here, though it would make the row
    // dispatchable and the tests tidier.
    //
    // The dispatcher fires at the stored zone's local hour. Writing UTC for
    // someone in the US means a 07:00 preference lands at 3am — which is the
    // precise complaint that started this whole line of work. That trades
    // "she gets nothing" for "Kin wakes her up at 3am", silently, and a bad
    // timezone is far harder to notice later than a missing one.
    //
    // So: leave it null, and make the failure LOUD. A null timezone is visible
    // (the RPCs exclude her, and the STEP 4 query counts her); a wrong one looks
    // like working software. This branch should be unreachable in any browser or
    // WKWebView that supports Intl, which is all of them we ship to.
    console.warn(
      "[deliveryChannel] could not resolve a timezone while enabling delivery — " +
        "notifications will not dispatch until one is set"
    );
  }

  return patch as Partial<UserPrefs>;
}
